import {
	PanelBody,
	SelectControl,
	RangeControl,
	FormTokenField,
} from '@wordpress/components';
import { __ } from '@wordpress/i18n';
import { useSelect } from '@wordpress/data';
import { store as coreStore } from '@wordpress/core-data';
import { useMemo } from '@wordpress/element';

const POST_TYPE_OPTIONS = [
	{ label: __( 'Posts', 'brandy-blocks' ), value: 'post' },
	{ label: __( 'Pages', 'brandy-blocks' ), value: 'page' },
];

const ORDER_OPTIONS = [
	{
		label: __( 'Newest to oldest', 'brandy-blocks' ),
		value: 'date/desc',
	},
	{
		label: __( 'Oldest to newest', 'brandy-blocks' ),
		value: 'date/asc',
	},
	{ label: __( 'A → Z', 'brandy-blocks' ), value: 'title/asc' },
	{ label: __( 'Z → A', 'brandy-blocks' ), value: 'title/desc' },
	{ label: __( 'Random', 'brandy-blocks' ), value: 'rand/desc' },
	{
		label: __( 'Menu order', 'brandy-blocks' ),
		value: 'menu_order/asc',
	},
];

export default function QueryControls( { attributes, setAttributes } ) {
	const query = attributes.query ?? {};

	const updateQuery = ( newQuery ) =>
		setAttributes( { query: { ...query, ...newQuery } } );

	// Check if WooCommerce is active by checking for product post type.
	const hasWooCommerce = useSelect( ( select ) => {
		const postTypes = select( coreStore ).getPostTypes( {
			per_page: -1,
		} );
		return postTypes?.some( ( pt ) => pt.slug === 'product' );
	}, [] );

	const postTypeOptions = useMemo( () => {
		const options = [ ...POST_TYPE_OPTIONS ];
		if ( hasWooCommerce ) {
			options.push( {
				label: __( 'Products', 'brandy-blocks' ),
				value: 'product',
			} );
		}
		return options;
	}, [ hasWooCommerce ] );

	// Fetch categories for the token field.
	const { categories, tags } = useSelect(
		( select ) => {
			const { getEntityRecords } = select( coreStore );
			return {
				categories:
					getEntityRecords( 'taxonomy', 'category', {
						per_page: 100,
					} ) ?? [],
				tags:
					getEntityRecords( 'taxonomy', 'post_tag', {
						per_page: 100,
					} ) ?? [],
			};
		},
		[]
	);

	const categoryNames = categories.map( ( c ) => c.name );
	const tagNames = tags.map( ( t ) => t.name );

	const selectedCategoryNames = ( query.categories ?? [] )
		.map( ( id ) => categories.find( ( c ) => c.id === id )?.name )
		.filter( Boolean );

	const selectedTagNames = ( query.tags ?? [] )
		.map( ( id ) => tags.find( ( t ) => t.id === id )?.name )
		.filter( Boolean );

	const postType = query.postType ?? 'post';
	const showCategoryFilter = postType === 'post';
	const showTagFilter = postType === 'post';

	return (
		<PanelBody
			title={ __( 'Query', 'brandy-blocks' ) }
			initialOpen={ true }
		>
			<SelectControl
				__nextHasNoMarginBottom
				__next40pxDefaultSize
				label={ __( 'Post type', 'brandy-blocks' ) }
				value={ postType }
				options={ postTypeOptions }
				onChange={ ( v ) =>
					updateQuery( {
						postType: v,
						categories: [],
						tags: [],
						productCategories: [],
					} )
				}
			/>
			<RangeControl
				__nextHasNoMarginBottom
				label={ __( 'Number of items', 'brandy-blocks' ) }
				value={ query.perPage ?? 6 }
				onChange={ ( v ) => updateQuery( { perPage: v } ) }
				min={ 1 }
				max={ 24 }
			/>
			<SelectControl
				__nextHasNoMarginBottom
				__next40pxDefaultSize
				label={ __( 'Order by', 'brandy-blocks' ) }
				value={ `${ query.orderBy ?? 'date' }/${ query.order ?? 'desc' }` }
				options={ ORDER_OPTIONS }
				onChange={ ( v ) => {
					const [ orderBy, order ] = v.split( '/' );
					updateQuery( { orderBy, order } );
				} }
			/>
			<RangeControl
				__nextHasNoMarginBottom
				label={ __( 'Offset', 'brandy-blocks' ) }
				value={ query.offset ?? 0 }
				onChange={ ( v ) => updateQuery( { offset: v } ) }
				min={ 0 }
				max={ 20 }
			/>
			{ showCategoryFilter && categories.length > 0 && (
				<FormTokenField
					label={ __( 'Categories', 'brandy-blocks' ) }
					value={ selectedCategoryNames }
					suggestions={ categoryNames }
					onChange={ ( tokens ) => {
						const ids = tokens
							.map(
								( name ) =>
									categories.find( ( c ) => c.name === name )
										?.id
							)
							.filter( Boolean );
						updateQuery( { categories: ids } );
					} }
					__experimentalExpandOnFocus
					__nextHasNoMarginBottom
				/>
			) }
			{ showTagFilter && tags.length > 0 && (
				<FormTokenField
					label={ __( 'Tags', 'brandy-blocks' ) }
					value={ selectedTagNames }
					suggestions={ tagNames }
					onChange={ ( tokens ) => {
						const ids = tokens
							.map(
								( name ) =>
									tags.find( ( t ) => t.name === name )?.id
							)
							.filter( Boolean );
						updateQuery( { tags: ids } );
					} }
					__experimentalExpandOnFocus
					__nextHasNoMarginBottom
				/>
			) }
		</PanelBody>
	);
}
